<template>
  <q-dialog ref="addMaterialsDialog" persistent>
    <q-card style="min-width: 410px">
      <q-card-section class="row items-center q-pb-none">
        <div class="text-h6">
          {{ isEdit ? 'Редактировать материалы' : 'Добавить материалы' }}
        </div>
        <q-space />
        <q-btn icon="close" flat round dense v-close-popup />
      </q-card-section>

      <q-linear-progress v-if="this.isFetching" indeterminate />
      <q-card-section v-else style="padding-bottom: 0;">
        <div style="margin-bottom: 20px;">
          <CustomSelect
            :value="materialSetId || ''"
            @change="onMaterialSetChange"
            :options="materialSetOptions"
            label="Выберите набор"
            optionValueKey="material_set_id"
            optionLabelKey="material_name"
            :loading="materialSetsFetching"
          />
        </div>
        <div v-if="!!materialSetId">
          <div
            v-for="(item, index) in this.formData.materials"
            :key="`material-${index}`"
            style="display: flex; align-items: flex-end; gap: 10px; margin-bottom: 20px;"
          >
            <div style="width: 250px;">
              <CustomSelect
                :value="item.material_id || ''"
                @change="value => onChange('material_id', value, index)"
                :options="materialsOptions"
                label="Материалы"
                optionValueKey="material_id"
                optionLabelKey="label"
                :loading="materialsLoading"
              />
            </div>

            <div style="width: 120px;">
              <CustomInput
                label="Количество"
                type="number"
                :min="0"
                :value="item.param_value"
                @change_input="(value) => onChange('param_value', value, index)"
              />
            </div>

            <q-btn
              v-if="!item.floor_param_material_id && this.showRemoveBtn"
              icon="close"
              flat
              round
              style="margin-bottom: 8px;"
              dense
              color="negative"
              @click="removeMaterial(index)"
              size="sm"
            />
          </div>
          <div
            style="display: flex; align-items: center; gap: 10px; margin-top: 10px;"
          >
            <q-btn
              icon="add"
              flat
              dense
              size="md"
              color="primary"
              @click="addNewMaterial"
              :disable="!canAddMoreMaterials"
              label="Добавить материал"
            />
          </div>
        </div>
      </q-card-section>
      <q-card-actions align="right" class="q-pa-md" style="padding-top: 0;">
        <CustomModalFooter
          :btn-loading="loading"
          :onCancel="closeDialog"
          :onSubmit="handleSave"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import CustomSelect from "@/components/form/CustomSelect";
import CustomInput from "@/components/form/CustomInput";
import { getPresetMaterials, getPresetMaterialSets, modifyFloorParamMaterials } from "@/views/ResidentialSettings/services";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import CustomModalFooter from "@/components/modal/modals/modal-footer";

export default {
  name: "FloorSchemaMaterialsModal",
  components: {
    CustomSelect,
    CustomInput,
    CustomModalFooter,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    floorParamId: {
      type: [String, Number],
      required: true,
    },
    floorMapId: {
      type: [String, Number],
      required: true,
    },
    editMaterials: {
      type: Object,
      default: null,
    },
    onSubmit: {
      type: Function,
    },
  },
  data() {
    return {
      loading: false,
      isFetching: false,
      materialSetsFetching: false,
      materialsLoading: false,
      materialSetOptions: [],
      materialsOptions: [],
      materialSetId: null,
      formData: {
        materials: [{material_set_id: '', material_id: '', param_value: 0}],
        param_value: 0,
      },
    };
  },
  computed: {
    isEdit() {
      return !!this.editMaterials;
    },
    isFormValid() {
      return this.formData.materials?.length > 0;
    },
    canAddMoreMaterials() {
      return this.formData.materials.length < this.materialsOptions.length;
    },
    showRemoveBtn() {
      return this.formData.materials?.filter(item => !item.floor_param_material_id)?.length > 1;
    },
  },
  methods: {
    async closeDialog() {
      this.$refs.addMaterialsDialog.hide()
    },
    async getMaterialSets() {
      this.materialSetsFetching = true;
      this.materialSetOptions = await getPresetMaterialSets(this.floorMapId);
      this.materialSetsFetching = false;
    },
    async loadMaterials() {
      this.materialsLoading = true;
      this.materialsOptions = await getPresetMaterials(this.materialSetId);
      this.materialsLoading = false;
    },
    onMaterialSetChange(value) {
      this.materialSetId = value
      this.formData.materials = [{material_set_id: '', material_id: '', param_value: 0}]
      this.loadMaterials()
    },
    onChange(type, value, index) {
      if(type === 'param_value' && (isNaN(Number(value)) || Number(value) < 0)) return
      this.formData.materials = this.formData.materials.map((item, idx) => idx === index ? ({...item, [type]: value}) : item)
    },
    addNewMaterial() {
      if (this.canAddMoreMaterials) {
        this.formData.materials.push({
          material_set_id: '',
          material_id: '',
          param_value: 0
        });
      }
    },
    removeMaterial(index) {
      if (this.formData.materials.length > 1) {
        this.formData.materials.splice(index, 1);
      }
    },
    fillEditData() {
      if (this.editMaterials) {
        this.formData.materials = this.editMaterials;
      }
    },
    resetForm() {
      this.formData = {
        materials: [{material_set_id: '', material_id: '', param_value: 0}],
      };
    },
    async handleSave() {
      if (!this.isFormValid) return;

      const materialSetIds = this.formData.materials
        .map(material => material.material_id)
        .filter(id => id !== '');

      const uniqueIds = new Set(materialSetIds);
      if (materialSetIds.length !== uniqueIds.size) {
        this.showErrorSnackbar('Нельзя добавлять одинаковые материалы');
        return;
      }

      const isAllFieldsFilled = this.formData.materials?.every(item => item.material_id && item.param_value)
      if (!isAllFieldsFilled) {
        this.showErrorSnackbar('Заполните данные');
        return;
      }

      const materials = this.formData.materials.map(material => ({
        floor_param_material_id: material.floor_param_material_id || null,
        material_set_id: this.materialSetId,
        material_id: material.material_id,
        param_value: material.param_value,
      }));

      this.loading = true;
      const res = await modifyFloorParamMaterials(this.floorParamId, { materials });
      this.loading = false;
      if(!res) return
      this.onSubmit(res)
      this.$refs.addMaterialsDialog.hide()
      this.showSucceedSnackbar()
    },
  },
  async mounted() {
    this.isFetching = true;
    await this.getMaterialSets();
    if(this.editMaterials) {
      this.formData.materials = this.editMaterials
      if(this.editMaterials[0]?.material_set_id) {
        this.materialSetId = this.editMaterials[0]?.material_set_id
        await this.loadMaterials()
      }
    }
    this.isFetching = false;
  },
  setup() {
    const {showSucceedSnackbar, showErrorSnackbar} = useCustomSnackbar()
    return {showSucceedSnackbar, showErrorSnackbar}
  }
};
</script>
